import { hasValue } from "@arti/utilities";
import React, { type FC, type ReactNode } from "react";

export interface PanelSectionProps {
  /**
   * Panel title
   */
  title?: string;

  /**
   * Badge or secondary content in the header
   */
  badge?: ReactNode;

  /**
   * Custom header content (overrides title and badge)
   */
  headerContent?: ReactNode;

  /**
   * Main panel content
   */
  children: ReactNode;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Additional CSS classes for the header
   */
  headerClassName?: string;

  /**
   * Additional CSS classes for the body
   */
  bodyClassName?: string;

  /**
   * Whether to show overflow scrolling
   */
  scrollable?: boolean;

  /**
   * Visual density
   * @default 'normal'
   */
  padding?: "none" | "compact" | "normal";
}

/**
 * PanelSection Component
 *
 * A compound component for creating consistent panel sections with optional headers and badges.
 *
 * @example
 * ```tsx
 * <PanelSection
 *   title="Node Telemetry"
 *   badge={<Badge>{nodes.length}</Badge>}
 * >
 *   <table>...</table>
 * </PanelSection>
 * ```
 */
export const PanelSection: FC<PanelSectionProps> = ({
  title,
  badge,
  headerContent,
  children,
  className = "",
  headerClassName = "",
  bodyClassName = "",
  scrollable = false,
  padding = "normal",
}) => {
  const paddingClasses = {
    compact: "!px-4 !py-2.5",
    none: "!p-0",
    normal: "", // Uses default .panel-header / .panel-body styles
  };

  return (
    <section className={`glass-panel overflow-hidden ${className}`.trim()}>
      {(hasValue(headerContent) || hasValue(title) || hasValue(badge)) && (
        <div
          className={`panel-header flex items-center justify-between ${paddingClasses[padding]} ${headerClassName}`.trim()}
        >
          {headerContent ?? (
            <>
              {title && (
                <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-gray-900 dark:text-white">
                  {title}
                </h2>
              )}
              {badge && <div className="panel-badge">{badge}</div>}
            </>
          )}
        </div>
      )}
      <div
        className={`panel-body ${scrollable ? "overflow-auto" : ""} ${paddingClasses[padding]} ${bodyClassName}`.trim()}
      >
        {children}
      </div>
    </section>
  );
};

/**
 * PanelHeader Subcomponent
 *
 * For cases where you need more control over the header content.
 */
export interface PanelHeaderProps {
  children: ReactNode;
  className?: string;
}

export const PanelHeader: FC<PanelHeaderProps> = ({
  children,
  className = "",
}) => <div className={`panel-header ${className}`.trim()}>{children}</div>;

/**
 * PanelBody Subcomponent
 *
 * For cases where you need more control over the body content.
 */
export interface PanelBodyProps {
  children: ReactNode;
  className?: string;
  scrollable?: boolean;
}

export const PanelBody: FC<PanelBodyProps> = ({
  children,
  className = "",
  scrollable = false,
}) => (
  <div
    className={`panel-body ${scrollable ? "overflow-auto" : ""} ${className}`.trim()}
  >
    {children}
  </div>
);

/**
 * Compound component type
 */
type PanelSectionComponent = FC<PanelSectionProps> & {
  Header: FC<PanelHeaderProps>;
  Body: FC<PanelBodyProps>;
};

/**
 * Compound exports for flexible usage
 */
const PanelSectionWithCompounds = Object.assign(PanelSection, {
  Body: PanelBody,
  Header: PanelHeader,
}) satisfies PanelSectionComponent;

export default PanelSectionWithCompounds;
